import { AlertTriangle, RefreshCcw } from "lucide-react";
import { useMemo } from "react";
import type { Location } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { DiscoveryDebugPanel } from "@/components/DiscoveryDebugPanel";
import { useAppCatalog } from "@/hooks/useAppCatalog";
import { resolveEntitlements } from "@/services/entitlementEngine";
import { readStoredAuthError } from "@/utils/samlDiagnostic";
import { isTroubleshootMode } from "@/utils/troubleshoot";

/**
 * Renders developer diagnostics when the URL includes the hidden
 * `who` + `troubleshoot` query gate. Mounted once at the app root so
 * the same diagnostics are available on every route (not only /myapps).
 *
 * On `/myapps`, only the route/session strip is shown here — My Apps
 * already runs `useAppCatalog` and renders `DiscoveryDebugPanel` so we
 * do not double-fetch Graph.
 */
export function GlobalTroubleshootPanel() {
  const location = useLocation();
  if (!isTroubleshootMode(location.search)) return null;
  if (location.pathname === "/myapps") {
    return <TroubleshootRouteAndSessionPanel />;
  }
  return <TroubleshootPanelBody />;
}

function TroubleshootRouteAndSessionPanel() {
  const location = useLocation();
  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 pb-6 pt-2">
      <TroubleshootSessionCard location={location} myAppsHint />
    </div>
  );
}

function TroubleshootPanelBody() {
  const location = useLocation();
  const { account, claims } = useAuth();
  const catalog = useAppCatalog();

  const resolution = useMemo(
    () => resolveEntitlements(catalog.apps, claims),
    [catalog.apps, claims],
  );

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 pb-6 pt-2">
      <TroubleshootSessionCard
        location={location}
        catalogError={catalog.error}
        catalogWarnings={catalog.warnings}
        catalogLoading={catalog.loading}
        onCatalogRefresh={catalog.refresh}
        showCatalogRefresh={!!account}
      />

      {account ? (
        <DiscoveryDebugPanel
          diagnostics={catalog.diagnostics}
          resolution={resolution}
          account={account}
          claims={claims}
        />
      ) : (
        <section className="card border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Sign in to load Microsoft Graph discovery and the full entitlement
          breakdown. Route and session error details above still apply on this
          page.
        </section>
      )}
    </div>
  );
}

type SessionCardProps = {
  location: Location;
  myAppsHint?: boolean;
  catalogError?: string | null;
  catalogWarnings?: string[];
  catalogLoading?: boolean;
  onCatalogRefresh?: () => void;
  showCatalogRefresh?: boolean;
};

function TroubleshootSessionCard({
  location,
  myAppsHint,
  catalogError,
  catalogWarnings = [],
  catalogLoading,
  onCatalogRefresh,
  showCatalogRefresh,
}: SessionCardProps) {
  const storedAuthError = readStoredAuthError();

  const oauthRedirectError = useMemo(() => {
    if (location.pathname !== "/auth/callback" || !location.hash) return null;
    const p = new URLSearchParams(location.hash.replace(/^#/, ""));
    const error = p.get("error");
    const description = p.get("error_description");
    if (!error && !description) return null;
    return {
      error,
      description: description
        ? decodeURIComponent(description.replace(/\+/g, " "))
        : null,
    };
  }, [location.pathname, location.hash]);

  const pathLine =
    location.pathname +
    (location.search || "") +
    (location.hash && location.pathname !== "/auth/callback"
      ? location.hash
      : "");

  const hasStoredFields =
    !!storedAuthError &&
    !!(
      storedAuthError.errorCode ||
      storedAuthError.errorMessage ||
      storedAuthError.correlationId
    );

  return (
    <section className="card border-violet-200 bg-violet-50/50 p-4 text-sm text-slate-800">
      <h2 className="flex items-center gap-2 text-base font-semibold text-violet-950">
        <AlertTriangle className="h-4 w-4 text-violet-600" />
        Troubleshoot mode — this page
      </h2>
      <p className="mt-1 text-xs text-violet-900/80">
        Append{" "}
        <code className="kbd">?who=seanmehrabi&amp;troubleshoot=1</code> to any
        portal URL (the typo <code className="kbd">trobleshoot</code> is also
        accepted). Details below are for the current route and session.
      </p>
      {myAppsHint ? (
        <p className="mt-2 text-xs text-violet-900/85">
          Graph / entitlement diagnostics for the catalog appear in the{" "}
          <strong>Developer troubleshoot panel</strong> further down on this
          page.
        </p>
      ) : null}

      <dl className="mt-3 grid grid-cols-1 gap-y-1 text-xs sm:grid-cols-[120px_1fr]">
        <dt className="font-semibold text-slate-500">Location</dt>
        <dd className="break-all font-mono text-[11.5px] text-slate-900">
          {pathLine || "(empty)"}
        </dd>
      </dl>

      {oauthRedirectError ? (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          <div className="font-semibold">OAuth redirect error (URL hash)</div>
          {oauthRedirectError.error ? (
            <div className="mt-1 font-mono">{oauthRedirectError.error}</div>
          ) : null}
          {oauthRedirectError.description ? (
            <div className="mt-1 whitespace-pre-wrap">
              {oauthRedirectError.description}
            </div>
          ) : null}
        </div>
      ) : null}

      {storedAuthError ? (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50/80 p-3 text-xs text-amber-950">
          <div className="font-semibold text-amber-900">
            Captured auth error (sessionStorage)
          </div>
          {hasStoredFields ? (
            <dl className="mt-2 grid gap-y-1 sm:grid-cols-[140px_1fr]">
              {storedAuthError.errorCode ? (
                <>
                  <dt className="text-slate-500">errorCode</dt>
                  <dd className="break-all font-mono">{storedAuthError.errorCode}</dd>
                </>
              ) : null}
              {storedAuthError.errorMessage ? (
                <>
                  <dt className="text-slate-500">errorMessage</dt>
                  <dd className="break-all whitespace-pre-wrap">
                    {storedAuthError.errorMessage}
                  </dd>
                </>
              ) : null}
              {storedAuthError.correlationId ? (
                <>
                  <dt className="text-slate-500">correlationId</dt>
                  <dd className="break-all font-mono">
                    {storedAuthError.correlationId}
                  </dd>
                </>
              ) : null}
              {storedAuthError.subError ? (
                <>
                  <dt className="text-slate-500">subError</dt>
                  <dd className="break-all font-mono">{storedAuthError.subError}</dd>
                </>
              ) : null}
              {storedAuthError.capturedAt ? (
                <>
                  <dt className="text-slate-500">capturedAt</dt>
                  <dd className="font-mono">{storedAuthError.capturedAt}</dd>
                </>
              ) : null}
            </dl>
          ) : (
            <p className="mt-2 text-amber-800/90">empty</p>
          )}
        </div>
      ) : (
        <div className="mt-3 rounded border border-slate-200 bg-white/80 p-3 text-xs text-slate-600">
          <div className="font-semibold text-slate-700">
            Captured auth error (sessionStorage)
          </div>
          <p className="mt-1">empty</p>
        </div>
      )}

      {catalogError ? (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <span className="font-semibold">Catalog error: </span>
          {catalogError}
        </div>
      ) : null}

      {catalogWarnings.length > 0 ? (
        <div className="mt-3 rounded border border-amber-200 bg-white p-3 text-xs text-slate-700">
          <div className="font-semibold text-amber-900">Catalog warnings</div>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {catalogWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {showCatalogRefresh && onCatalogRefresh ? (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onCatalogRefresh}
            className="btn-ghost inline-flex items-center gap-1.5 text-xs"
            disabled={catalogLoading}
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            {catalogLoading ? "Refreshing catalog…" : "Refresh catalog / Graph"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
